import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { AccessCentrePermission } from 'domains/accessCentre/types';
import PermissionCard from '../PermissionCard';

/** Human-readable module name (e.g. "access_management" → "Access management"). */
const formatModuleName = (moduleKey: string): string => {
    if (!moduleKey) return moduleKey;
    const withSpaces = moduleKey.replace(/_/g, ' ');
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
};

/** Display name for a permission: part after the first '.' in key (e.g. "access_management.view" → "view"). */
const getPermissionLabel = (permission: AccessCentrePermission): string => {
    const afterDot = permission.key.includes('.') ? permission.key.split('.').slice(1).join('.') : permission.key;
    return formatModuleName(afterDot) || permission.name;
};

const permissionsGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 1.5,
};

export type PermissionModuleCardProps = {
    moduleKey: string;
    permissions: AccessCentrePermission[];
    selectedPermissionIds: number[];
    onTogglePermission: (id: number) => void;
};

const PermissionModuleCard = ({
    moduleKey,
    permissions,
    selectedPermissionIds,
    onTogglePermission,
}: PermissionModuleCardProps) => (
    <Accordion
        disableGutters
        elevation={0}
        sx={{
            '&:before': { display: 'none' },
            borderBottom: '1px solid',
            borderColor: 'divider',
        }}
    >
        <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${moduleKey}-permissions`}
            id={`${moduleKey}-header`}
            data-testid={`access-center-permissions-module-${moduleKey}`}
        >
            <Typography variant="subtitle1" fontWeight={500}>
                {formatModuleName(moduleKey)}
            </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
            <Box sx={permissionsGridStyles}>
                {permissions.map((permission) => (
                    <PermissionCard
                        key={permission.id}
                        permission={permission}
                        label={getPermissionLabel(permission)}
                        isSelected={selectedPermissionIds.includes(permission.id)}
                        onToggle={onTogglePermission}
                    />
                ))}
            </Box>
        </AccordionDetails>
    </Accordion>
);

export default PermissionModuleCard;
